import { Button, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { ContentService, type QuestionPublic, QuestionsService } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import { TopicSelector } from "@/components/Questions/TopicSelector"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { toaster } from "@/components/ui/toaster"
import { handleError } from "@/utils"

interface AssignTopicsModalProps {
  questionId: string | null
  isOpen: boolean
  onClose: () => void
  defaultTopic?: { id: string; name: string }
}

export function AssignTopicsModal({
  questionId,
  isOpen,
  onClose,
  defaultTopic,
}: AssignTopicsModalProps) {
  const queryClient = useQueryClient()
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])

  const { data: question, isLoading } = useQuery<QuestionPublic, ApiError>({
    queryKey: ["question-detail", questionId],
    queryFn: () => QuestionsService.getQuestion({ questionId: questionId! }),
    enabled: isOpen && Boolean(questionId),
  })

  useEffect(() => {
    if (question) {
      const existingTopics =
        question.topics?.map((topic) => ({
          id: topic.id,
          name: topic.name,
        })) ?? []
      setSelectedTopics(existingTopics)
    } else if (!isOpen) {
      setSelectedTopics([])
    }
  }, [question, isOpen])

  const selectedTopicIds = useMemo(
    () => new Set(selectedTopics.map((topic) => topic.id)),
    [selectedTopics],
  )

  useEffect(() => {
    if (
      isOpen &&
      defaultTopic &&
      !selectedTopicIds.has(defaultTopic.id) &&
      question
    ) {
      setSelectedTopics((prev) => [...prev, defaultTopic])
    }
  }, [defaultTopic, isOpen, question, selectedTopicIds])

  const updateMutation = useMutation({
    mutationFn: (topicIds: string[]) =>
      ContentService.overwriteQuestionTopics({
        questionId: questionId!,
        requestBody: { topic_ids: topicIds },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["topic-questions"] })
      queryClient.invalidateQueries({ queryKey: ["untagged-questions"] })
    },
  })

  const handleSave = async () => {
    if (!questionId) {
      return
    }

    try {
      await updateMutation.mutateAsync(selectedTopics.map((topic) => topic.id))
      toaster.success({
        title: "Topics updated",
        description: "Question topics have been updated successfully.",
      })
      onClose()
    } catch (err) {
      handleError(err as ApiError)
    }
  }

  const isPrimaryTopicMissing = Boolean(
    defaultTopic && !selectedTopicIds.has(defaultTopic.id),
  )

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent maxW="lg">
        <DialogHeader>
          <DialogTitle>Assign Topics</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text color="fg.muted">
              Select all topics that apply to this question. Removing every
              topic will move the question to the &ldquo;no topics&rdquo; list.
            </Text>
            <TopicSelector
              allowCreate
              selectedTopics={selectedTopics}
              onChange={setSelectedTopics}
            />
            {isPrimaryTopicMissing && defaultTopic && (
              <Text fontSize="sm" color="orange.500">
                Include {defaultTopic.name} if you want the question to remain
                in this topic.
              </Text>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleSave}
            loading={updateMutation.isPending || isLoading}
            disabled={!selectedTopics || updateMutation.isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
